import { isPlatformServer } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';

import { Observable, publishReplay, refCount, switchMap } from 'rxjs';

import { TimerService } from '../browser/timer.service';
import { ClientConfigProductName } from '../client-config/client-config-product-name.model';
import { Page } from '../client-config/page.client-config';
import { DslService } from '../dsl/dsl.service';
import { HostApiService } from '../http/host-api.service';
import { ProductService } from '../products/product.service';

/**
 * @stable
 */
export interface GetJsonOptions {
    filterOnClient?: boolean;
    product?: ClientConfigProductName;
}

/**
 * @whatItDoes Queries server for Sitecore content.
 *
 * @howToUse
 *
 * ```
 * contentService.getJson('App-v1.0/Some/Content').subscribe((content) => {
 *     this.content = content;
 * })
 * ```
 *
 * @stable
 */
@Injectable({
    providedIn: 'root',
})
export class ContentService {
    private readonly isServer = isPlatformServer(inject(PLATFORM_ID));
    private readonly expiry = this.isServer ? 1000 : 5 * 60 * 1000; // 5min
    private readonly cachedResponses = new Map<string, Observable<any>>();

    private readonly apiService = inject(HostApiService);
    private readonly timerService = inject(TimerService);
    private readonly dslService = inject(DslService);
    private readonly productService = inject(ProductService);
    private readonly page = inject(Page);

    getJson(path: string, options?: GetJsonOptions): Observable<any> {
        options = options || {};
        if (!this.cachedResponses.has(path)) {
            const productMetadata = options.product ? this.productService.getMetadata(options.product) : null;
            const productEnabled = productMetadata?.isEnabled === true;
            const apiBaseUrl = productEnabled ? productMetadata.apiBaseUrl : this.productService.current.apiBaseUrl;
            const request = this.apiService
                .get(
                    'content',
                    { path, filterOnClient: options.filterOnClient },
                    {
                        baseUrl: apiBaseUrl,
                        headers: productEnabled ? { [`x-bwin-${options.product}-api`]: this.page.environment } : {},
                    },
                )
                .pipe(publishReplay(1), refCount());

            this.cachedResponses.set(path, request);

            this.timerService.setTimeoutOutsideAngularZone(() => this.evictFromCache(path), this.expiry);
        }

        return this.cachedResponses.get(path)!;
    }

    getJsonFiltered<T>(path: string, product?: ClientConfigProductName): Observable<T> {
        return this.getJson(path, product ? { filterOnClient: true, product } : { filterOnClient: true }).pipe(
            switchMap((c) => this.dslService.evaluateContent<T>(c)),
        );
    }

    private evictFromCache(path: string) {
        this.cachedResponses.delete(path);
    }
}
